// src/utils/permissions.js
//
// Single source of truth for the app's Role Matrix (Role-Matrix-v4.docx),
// mirrored on the backend by PERMISSION_MATRIX in app.py -- keep the two
// in sync if this table ever changes. Every tier name here must match a
// RoleName row exactly as it comes back from the login/OTP response and
// gets cached in localStorage("currentUser").RoleName (see Layout.jsx) --
// those rows are the same four the Manage Roles page assigns via
// GET /permission-tiers ("View" / "Contributor" / "Admin" / "Super Admin").
//
// The matrix's "own data only" scoping for Contributor (Dashboard
// "View - Own", Project/Cost Estimation "Add & edit own") is enforced,
// but NOT here -- it's server-side, in app.py's
// check_contributor_owns_project() plus the CreatedByUserID filter on
// /projects, /dashboard/stats and /quick-search (see
// add_project_owner_column.sql). This file still grants Contributor the
// same visible/add/edit/delete flags as Admin below -- what actually
// differs for Contributor is which ROWS the API returns/accepts, not
// which buttons this file shows, so there's nothing to change here for
// it. AI Assistant's "limited to own data" scoping is still not
// implemented (its results aren't tied to specific project rows the
// same way).
export const TIERS = ["View", "Contributor", "Admin", "Super Admin"];

// visible: can the user reach this module/page at all.
// add / edit / delete: can they use that action once there (omitted /
// false = hidden). A module with visible:false should never grant any
// action -- always check isVisible() before add/edit/delete in code
// that also needs to gate the page itself, not just a button.
export const PERMISSIONS = {
  Dashboard: {
    View: { visible: true },
    Contributor: { visible: true },
    Admin: { visible: true },
    "Super Admin": { visible: true },
  },
  Project: {
    View: { visible: true, add: false, edit: false, delete: false },
    Contributor: { visible: true, add: true, edit: true, delete: false },
    Admin: { visible: true, add: true, edit: true, delete: true },
    "Super Admin": { visible: true, add: true, edit: true, delete: true },
  },
  // The 7-step Cost Estimation wizard (Step1-5 + BasisOfCost/
  // ElementScopePage1/EstimationSummary) -- View can look at finished
  // estimations (EstimationSummary, reached from Project's own View
  // action) but not open the wizard itself to create/edit one.
  CostEstimation: {
    View: { visible: false, add: false, edit: false },
    Contributor: { visible: true, add: true, edit: true },
    Admin: { visible: true, add: true, edit: true },
    "Super Admin": { visible: true, add: true, edit: true },
  },
  // Per explicit request, Rate Card delete is Super Admin-only -- Admin
  // keeps add/edit but no longer sees/can use the delete action here.
  // Mirrored server-side on DELETE /rate-card/<rc_id> (see
  // delete_rate_card() in app.py's @require_tier decorator) -- keep
  // both in sync, since this file alone only hides the button and
  // doesn't stop a direct API call.
  RateCard: {
    View: { visible: false, add: false, edit: false, delete: false },
    Contributor: { visible: true, add: true, edit: false, delete: false },
    Admin: { visible: true, add: true, edit: true, delete: false },
    "Super Admin": { visible: true, add: true, edit: true, delete: true },
  },
  AIAssistant: {
    View: { visible: true },
    Contributor: { visible: true },
    Admin: { visible: true },
    "Super Admin": { visible: true },
  },
  // Master data: Role / Department / Company CRUD pages under the
  // Master flyout.
  Master: {
    View: { visible: false, add: false, edit: false, delete: false },
    Contributor: { visible: false, add: false, edit: false, delete: false },
    Admin: { visible: true, add: true, edit: true, delete: false },
    "Super Admin": { visible: true, add: true, edit: true, delete: true },
  },
  // Account -> Users + Manage Roles (assigning a tier is an "edit"
  // action on a user, so it follows the same add/edit/delete gate).
  AccountUsers: {
    View: { visible: false, add: false, edit: false, delete: false },
    Contributor: { visible: false, add: false, edit: false, delete: false },
    Admin: { visible: true, add: true, edit: true, delete: false },
    "Super Admin": { visible: true, add: true, edit: true, delete: true },
  },
  // Mail Configuration + templates.
  Configuration: {
    View: { visible: false, add: false, edit: false, delete: false },
    Contributor: { visible: false, add: false, edit: false, delete: false },
    Admin: { visible: false, add: false, edit: false, delete: false },
    "Super Admin": { visible: true, add: true, edit: true, delete: true },
  },
  Help: {
    View: { visible: true },
    Contributor: { visible: true },
    Admin: { visible: true },
    "Super Admin": { visible: true },
  },
};

// Reads fresh every call (not cached at import time) -- Layout.jsx
// re-reads localStorage on every render too, so this stays in step
// with whatever's currently cached there. A role change made via
// Manage Roles only reaches this after the affected user logs out and
// back in (the tier is stamped into both the JWT and this cached
// object at login -- see generate_jwt()/verify_otp() in app.py).
export function getCurrentTier() {
  try {
    const stored = JSON.parse(localStorage.getItem("currentUser") || "null");
    return stored?.RoleName || null;
  } catch {
    return null;
  }
}

// Any RoleName that isn't one of the four matrix tiers (a leftover
// custom role from before Manage Roles existed, or no role at all) --
// falls back to "View"'s permissions, the least-privileged real tier,
// rather than silently granting full access. Once every user has been
// swept through Manage Roles and assigned a real tier, this fallback
// stops mattering.
function tierPermissions(module) {
  const tier = getCurrentTier();
  const table = PERMISSIONS[module];
  if (!table) return { visible: true }; // unknown module name -- fail open on visibility, no actions
  return table[tier] || table.View;
}

export function isModuleVisible(module) {
  return !!tierPermissions(module).visible;
}

export function canAdd(module) {
  const perm = tierPermissions(module);
  return !!perm.visible && !!perm.add;
}

export function canEdit(module) {
  const perm = tierPermissions(module);
  return !!perm.visible && !!perm.edit;
}

export function canDelete(module) {
  const perm = tierPermissions(module);
  return !!perm.visible && !!perm.delete;
}
